import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Scale, Award, Stethoscope, Heart, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PracticeAreaFAQ from '@/components/PracticeAreaFAQ';

const medMalFAQs = [
  {
    question: "What types of medical malpractice cases do you handle?",
    answer: "I focus on clear-cut cases where liability is obvious—situations like surgical instruments left inside a patient, wrong-site surgery, medication errors with clear documentation, or obvious misdiagnosis with undeniable evidence."
  },
  {
    question: "Why do you only take 'clear liability' cases?",
    answer: "Medical malpractice cases are extremely expensive to litigate, often requiring $50,000-$100,000+ in expert witness fees. By focusing on clear-cut cases, I can pursue justice efficiently without the risk of prolonged, expensive battles."
  },
  {
    question: "What is considered 'clear liability' in medical malpractice?",
    answer: "Clear liability means the negligence is obvious: retained surgical instruments, wrong-site surgery, clear medication errors documented in records, or obvious 'never events' that should never occur with proper care."
  },
  {
    question: "How long do I have to file a medical malpractice claim?",
    answer: "Washington has a 3-year statute of limitations for medical malpractice (RCW 4.16.350), but exceptions apply. The clock may start from when you discovered the injury. Contact me promptly to protect your rights."
  }
];

const MedicalMalpracticePage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="Medical Malpractice Attorney | Clear Liability Cases | Amy C. Brown Law"
        description="Washington medical malpractice attorney handling clear-cut cases: retained surgical instruments, wrong-site surgery, obvious medical errors. Free consultation."
      />
      
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              Clear Liability Cases Only
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Medical Malpractice
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              When medical professionals make obvious, undeniable errors—like leaving surgical instruments inside a patient—the harm is clear and so is the path to justice.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Cases I Take vs Don't Take */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
              My Honest Approach
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Medical malpractice cases are among the most expensive to litigate. I take cases where liability is clear and undeniable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-teal-50 rounded-2xl p-8 border border-teal-200">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="w-8 h-8 text-teal-600" />
                <h3 className="font-heading text-xl font-bold text-secondary">Cases I Take</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-bold">+</span>
                  <span className="text-slate-700">Surgical instruments left in the body</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-bold">+</span>
                  <span className="text-slate-700">Wrong-site surgery</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-bold">+</span>
                  <span className="text-slate-700">Clear medication errors with documentation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-bold">+</span>
                  <span className="text-slate-700">Obvious misdiagnosis with undeniable evidence</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="w-8 h-8 text-slate-400" />
                <h3 className="font-heading text-xl font-bold text-secondary">Cases I Typically Do Not Take</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-slate-400 font-bold">-</span>
                  <span className="text-slate-500">Bad outcome despite proper treatment</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-slate-400 font-bold">-</span>
                  <span className="text-slate-500">Disputed diagnosis where experts disagree</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-slate-400 font-bold">-</span>
                  <span className="text-slate-500">Cases requiring extensive expert battles</span>
                </li>
              </ul>
              <p className="text-sm text-slate-500 mt-6 italic">
                If your case does not fit, I will be honest and may refer you to specialists.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Retained Surgical Instruments */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-coral-100 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                Clear Liability Example
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Retained Surgical Instruments
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                When a surgical team leaves a sponge, clamp, or other instrument inside a patient, it is a never event—a mistake that should never happen with proper procedures.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                These cases often present clear liability because X-rays show the object, surgical counts should have caught it, and the harm is directly attributable.
              </p>
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-coral-600 transition-all"
              >
                <Phone className="w-5 h-5" />
                Free Case Evaluation
              </a>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
                <Stethoscope className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Medical Record Review</h4>
                <p className="text-sm text-slate-600">I analyze your records to identify clear evidence</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
                <Scale className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">No Fees Unless You Win</h4>
                <p className="text-sm text-slate-600">Contingency fee basis</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
                <Award className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Selective Case Review</h4>
                <p className="text-sm text-slate-600">I only take cases I believe in</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
                <Heart className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Personal Attention</h4>
                <p className="text-sm text-slate-600">Work directly with me</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PracticeAreaFAQ
        title="Medical Malpractice FAQs"
        subtitle="Common questions about medical negligence claims in Washington State."
        faqs={medMalFAQs}
        accentColor="teal"
        schemaId="med-malpractice"
      />

      {/* Case Result blurb — social proof for med mal visitors */}
      {/* HIDDEN — Case study pages are temporarily hidden pending review.
      <section className="py-14 md:py-16 bg-slate-50 border-t border-slate-100" data-testid="med-mal-case-result-blurb">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 font-semibold mb-3">
            <Award className="w-4 h-4" /> Case Result
          </div>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
                Six-Figure Settlement for Retained Surgical-Tool Fragment
              </h2>
              <p className="text-slate-600 leading-relaxed">
                A broken surgical-tool fragment caused five days of severe pain and required a second operation. Amy obtained a confidential six-figure settlement without filing a lawsuit.
              </p>
            </div>
            <Link
              to="/case-results/retained-surgical-tool-settlement"
              data-testid="med-mal-case-result-link"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition whitespace-nowrap"
            >
              Read the Case Study <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Every case is different. Past results do not guarantee similar outcomes.
          </p>
        </div>
      </section>
      */}

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Case Evaluation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I will review your situation honestly and tell you if I think you have a viable case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-6 h-6" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MedicalMalpracticePage;
